import { useState } from 'react';
import { createPortal } from 'react-dom';
import { CreditCard, DollarSign } from 'lucide-react';
import type { EstimateDto } from '@/types';
import { useEstimatePayable } from '@/hooks/usePayable';
import { useJobEditPermissions } from '@/hooks/useJobEditPermissions';
import { roundCurrency } from '@/lib/invoiceCalculations';
import { PayablePaymentCard } from './PaymentCard';
import { TakePaymentModal } from './TakePaymentModal';
import { PaymentStatusBadge } from './ui/PaymentStatusBadge';

interface EstimatePaymentsSectionProps {
    estimate: EstimateDto;
    /** Opens the job the estimate was converted to. */
    onViewJob?: (jobId: string) => void;
}

/**
 * Payments taken on an estimate (e.g. a deposit): summary and the payment list. Taking a payment
 * is EstimateTakePaymentButton, which lives in the modal's action bar like the invoice's.
 * Once converted the payments belong to the job too, so this turns read-only and points there.
 */
export function EstimatePaymentsSection({ estimate, onViewJob }: EstimatePaymentsSectionProps) {
    const payable = useEstimatePayable(estimate);
    const { canEditField } = useJobEditPermissions();

    const isConverted = estimate.status === 'Converted';
    const canEditPayments = canEditField('payments');
    const amountPaid = estimate.amount_paid ?? 0;
    const balance = roundCurrency(payable.total - amountPaid);

    return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="mb-3">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-gray-600" />
                    Payments
                    {estimate.payment_status && <PaymentStatusBadge status={estimate.payment_status} />}
                </h3>
            </div>

            <div className="flex justify-between text-sm pb-3 border-b border-gray-100">
                <span className="text-gray-600">Total: <span className="font-medium text-gray-900">${payable.total.toFixed(2)}</span></span>
                <span className="text-gray-600">Paid: <span className="font-medium text-green-600">${amountPaid.toFixed(2)}</span></span>
                <span className="text-gray-600">Balance: <span className={`font-medium ${balance > 0 ? 'text-orange-600' : 'text-green-600'}`}>${balance.toFixed(2)}</span></span>
            </div>

            {isConverted && (
                <p className="mt-3 text-xs text-gray-500">
                    Payments moved to the job — take further payments there.
                    {estimate.converted_to_job_id && onViewJob && (
                        <>
                            {' '}
                            <button
                                type="button"
                                onClick={() => onViewJob(estimate.converted_to_job_id!)}
                                className="text-primary-600 hover:underline font-medium"
                            >
                                View job
                            </button>
                        </>
                    )}
                </p>
            )}

            <PayablePaymentCard payable={payable} compact disabled={isConverted || !canEditPayments} depositVariant="panel" />
        </div>
    );
}

interface EstimateTakePaymentButtonProps {
    estimate: EstimateDto;
    /** Custom render (e.g. the mobile footer). Defaults to the header-style button. */
    renderCustom?: (onClick: () => void, isDisabled: boolean) => React.ReactNode;
}

/** Opens TakePaymentModal for an estimate; disabled once converted, fully paid or nothing is owed. */
export function EstimateTakePaymentButton({ estimate, renderCustom }: EstimateTakePaymentButtonProps) {
    const payable = useEstimatePayable(estimate);
    const { canEditField } = useJobEditPermissions();
    const [showTakePayment, setShowTakePayment] = useState(false);

    const isConverted = estimate.status === 'Converted';
    const balance = roundCurrency(payable.total - (estimate.amount_paid ?? 0));
    const isDisabled = isConverted || !canEditField('payments') || estimate.payment_status === 'PaidFully' || balance <= 0;
    const open = () => setShowTakePayment(true);

    return (
        <>
            {renderCustom ? renderCustom(open, isDisabled) : (
                <button
                    type="button"
                    onClick={open}
                    disabled={isDisabled}
                    title={isConverted ? 'Payments moved to the job — take further payments there.' : 'Take payment'}
                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                    <DollarSign className="h-4 w-4" />
                    Take Payment
                </button>
            )}

            {/* Portaled to body like the estimate modal, so the inherited layer depth stacks it above. */}
            {createPortal(
                <TakePaymentModal
                    isOpen={showTakePayment}
                    onClose={() => setShowTakePayment(false)}
                    payable={payable}
                />,
                document.body
            )}
        </>
    );
}
